import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Loader2, CheckCircle, AlertTriangle, Clock, Send, Building, Package, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CotizarPublico() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [precios, setPrecios] = useState([]);
  const [plazoEntrega, setPlazoEntrega] = useState('');
  const [condicionPago, setCondicionPago] = useState('');
  const [obsGeneral, setObsGeneral] = useState('');
  const [contactoNombre, setContactoNombre] = useState('');
  const [contactoTelefono, setContactoTelefono] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/api/public/cotizar/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || 'Enlace no valido');
          setLoading(false);
          return;
        }
        const d = await res.json();
        setData(d);
        setPrecios(d.items.map((it, idx) => ({
          item_index: idx,
          precio_unitario: '',
          observacion: '',
        })));
      } catch {
        setError('Error al cargar la solicitud');
      }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  const updatePrecio = (idx, field, value) => {
    setPrecios(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleSubmit = async () => {
    const hasAnyPrice = precios.some(p => p.precio_unitario && parseFloat(p.precio_unitario) > 0);
    if (!hasAnyPrice) {
      setError('Ingrese al menos un precio unitario');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setSending(true);
    try {
      const body = {
        precios: precios.map(p => ({
          item_index: p.item_index,
          precio_unitario: parseFloat(p.precio_unitario) || 0,
          observacion: p.observacion || '',
        })),
        plazo_entrega: plazoEntrega,
        condicion_pago: condicionPago,
        observacion_general: obsGeneral,
        contacto_nombre: contactoNombre,
        contacto_telefono: contactoTelefono,
      };
      const res = await fetch(`${API}/api/public/cotizar/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Error al enviar');
        setSending(false);
        return;
      }
      setSent(true);
    } catch {
      setError('Error de conexion');
    }
    setSending(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Enlace no disponible</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Already responded
  if (data?.ya_respondida) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header numero={data.numero} proveedorNombre={data.proveedor_nombre} />
        <div className="max-w-2xl mx-auto p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-900 mb-2">Cotizacion ya registrada</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Su respuesta para la solicitud <strong>{data.numero}</strong> ya fue registrada anteriormente.
            </p>
            <p className="text-xs text-muted-foreground">
              Si necesita modificar su cotizacion, contacte a compras@chirinosteel.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Expired
  if (data?.expirado) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header numero={data.numero} proveedorNombre={data.proveedor_nombre} />
        <div className="max-w-2xl mx-auto p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <Clock className="w-14 h-14 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-900 mb-2">Plazo vencido</h2>
            <p className="text-sm text-muted-foreground">
              El plazo para responder a la solicitud <strong>{data.numero}</strong> ha vencido
              ({formatDate(data.fecha_vencimiento)}).
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Contacte a compras@chirinosteel.com si aun desea cotizar.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (sent) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header numero={data.numero} proveedorNombre={data.proveedor_nombre} />
        <div className="max-w-2xl mx-auto p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-9 h-9 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Cotizacion Enviada</h2>
            <p className="text-sm text-muted-foreground">
              Su cotizacion para la solicitud <strong>{data.numero}</strong> ha sido registrada exitosamente.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              El equipo de compras de CHIRINO STEEL revisara su propuesta.
            </p>
            <div className="mt-6 bg-gray-50 rounded-lg p-4 text-sm text-left">
              <p className="text-muted-foreground">Contacto:</p>
              <p className="font-medium text-[#003366]">compras@chirinosteel.com</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gray-50" data-testid="cotizar-publico-page">
      <Header numero={data.numero} proveedorNombre={data.proveedor_nombre} />

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Info bar */}
        <div className="flex flex-wrap gap-4 text-sm">
          {data.fecha_vencimiento && (
            <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-amber-800">Responder antes del <strong>{formatDate(data.fecha_vencimiento)}</strong></span>
            </div>
          )}
          <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
            <Package className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800">{data.items.length} item(s) a cotizar</span>
          </div>
        </div>

        {data.mensaje_adicional && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            {data.mensaje_adicional}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
        )}

        {/* Items table - compact grid format */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm" data-testid="items-table">
            <thead>
              <tr className="bg-[#003366] text-white text-xs">
                <th className="px-2 py-2 text-center w-8">#</th>
                <th className="px-2 py-2 text-left">Descripcion</th>
                <th className="px-2 py-2 text-center w-16">Cant.</th>
                <th className="px-2 py-2 text-center w-12">Und</th>
                <th className="px-2 py-2 text-left w-36">Precio Unit. (CLP neto) *</th>
                <th className="px-2 py-2 text-left w-44">Observacion</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.items.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50/50' : ''} data-testid={`item-row-${idx}`}>
                  <td className="px-2 py-2 text-center text-xs text-muted-foreground font-mono">{idx + 1}</td>
                  <td className="px-2 py-2">
                    <p className="font-medium text-gray-900 text-sm leading-tight">{item.descripcion}</p>
                    <div className="flex gap-2 mt-0.5">
                      {item.codigo && <span className="text-[10px] font-mono text-muted-foreground bg-gray-100 px-1 rounded">{item.codigo}</span>}
                      {item.observacion_solicitante && (
                        <span className="text-[10px] text-blue-600 italic">{item.observacion_solicitante}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center font-semibold">{item.cantidad}</td>
                  <td className="px-2 py-2 text-center text-xs text-muted-foreground">{item.unidad}</td>
                  <td className="px-2 py-1.5">
                    <Input
                      type="number"
                      value={precios[idx]?.precio_unitario || ''}
                      onChange={e => updatePrecio(idx, 'precio_unitario', e.target.value)}
                      placeholder="$0"
                      className="text-sm h-8"
                      data-testid={`precio-${idx}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={precios[idx]?.observacion || ''}
                      onChange={e => updatePrecio(idx, 'observacion', e.target.value)}
                      placeholder="Marca, plazo..."
                      className="text-sm h-8"
                      data-testid={`obs-${idx}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Additional info */}
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Informacion Adicional</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-0.5 block">Plazo de Entrega</label>
              <Input value={plazoEntrega} onChange={e => setPlazoEntrega(e.target.value)}
                placeholder="Ej: 5 dias habiles" className="text-sm" data-testid="plazo-entrega" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-0.5 block">Condicion de Pago</label>
              {/* Iter 286.n — dropdown desde catálogo condiciones_pago (13 vigentes) */}
              <select
                value={condicionPago}
                onChange={e => setCondicionPago(e.target.value)}
                className="w-full h-9 px-3 rounded-md border text-sm bg-white"
                data-testid="condicion-pago"
              >
                <option value="">Seleccione...</option>
                {(data?.condiciones_pago || []).map(cp => (
                  <option key={cp.codigo} value={cp.nombre}>{cp.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-0.5 block">Nombre Contacto</label>
              <Input value={contactoNombre} onChange={e => setContactoNombre(e.target.value)}
                placeholder="Su nombre" className="text-sm" data-testid="contacto-nombre" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-0.5 block">Telefono Contacto</label>
              <Input value={contactoTelefono} onChange={e => setContactoTelefono(e.target.value)}
                placeholder="+56 9 ..." className="text-sm" data-testid="contacto-telefono" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-0.5 block">Observacion General (opcional)</label>
            <textarea value={obsGeneral} onChange={e => setObsGeneral(e.target.value)}
              placeholder="Cualquier informacion adicional sobre su cotizacion..."
              className="w-full h-20 px-3 py-2 rounded-md border text-sm resize-none"
              data-testid="obs-general" />
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end pb-8">
          <Button onClick={handleSubmit} disabled={sending}
            className="bg-[#003366] hover:bg-[#002244] gap-2 px-8 py-2.5 text-base" data-testid="btn-enviar">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            Enviar Cotizacion
          </Button>
        </div>
      </div>
    </div>
  );
}

// Header component
function Header({ numero, proveedorNombre }) {
  return (
    <div className="bg-[#003366] text-white">
      <div className="max-w-4xl mx-auto px-4 py-5 flex items-center gap-4">
        <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
          <Building className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-lg font-bold">CHIRINO STEEL</h1>
          <p className="text-sm opacity-80">
            Solicitud de Cotización {numero}
            {proveedorNombre && <span> — {proveedorNombre}</span>}
          </p>
        </div>
      </div>
    </div>
  );
}
